import type { FC } from "react"
import type { FlexProps } from "@chakra-ui/react"
import { Flex } from "@chakra-ui/react"
import invariant from "tiny-invariant"

import type { Proposal } from "query/graphql"
import { ProposalStatus } from "query/graphql"
import ProposalPendingCTA from "proposal/components/ctas/ProposalPendingCTA"
import ProposalActiveCTA from "proposal/components/ctas/ProposalActiveCTA"
import ProposalSuccededCTA from "proposal/components/ctas/ProposalSuccededCTA"
import ProposalQueuedCTA from "proposal/components/ctas/ProposalQueuedCTA"
import ProposalExecutedCTA from "proposal/components/ctas/ProposalExecutedCTA"
import ProposalDefeatedCTA from "proposal/components/ctas/ProposalDefeatedCTA"
import ProposalCanceledCTA from "proposal/components/ctas/ProposalCanceledCTA"
import ProposalExpiredCTA from "proposal/components/ctas/ProposalExpiredCTA"

type ProposalOnChainCTAProps = {
  proposal: Proposal
  slug: string
}

const ProposalOnChainCTA: FC<ProposalOnChainCTAProps & FlexProps> = ({
  proposal,
  slug,
  ...flexProps
}) => {
  const getCTAComponent = () => {
    if (proposal.status === ProposalStatus.Pending)
      return <ProposalPendingCTA proposalId={proposal.id} slug={slug} />

    if (
      proposal.status === ProposalStatus.Active ||
      proposal.status === ProposalStatus.Extended
    )
      return <ProposalActiveCTA proposalId={proposal.id} slug={slug} />

    if (proposal.status === ProposalStatus.Succeeded)
      return <ProposalSuccededCTA proposalId={proposal.id} slug={slug} />

    if (
      proposal.status === ProposalStatus.Queued ||
      proposal.status === ProposalStatus.Pendingexecution
    )
      return <ProposalQueuedCTA proposalId={proposal.id} slug={slug} />

    if (
      proposal.status === ProposalStatus.Executed ||
      proposal.status === ProposalStatus.Crosschainexecuted
    )
      return <ProposalExecutedCTA proposalId={proposal.id} slug={slug} />

    if (proposal.status === ProposalStatus.Defeated)
      return <ProposalDefeatedCTA proposalId={proposal.id} slug={slug} />

    if (proposal.status === ProposalStatus.Canceled) {
      invariant(proposal.onchainId, "Proposal onchainId is required")

      return <ProposalCanceledCTA proposalId={proposal.onchainId} slug={slug} />
    }

    if (proposal.status === ProposalStatus.Expired) {
      invariant(proposal.onchainId, "Proposal onchainId is required")

      return <ProposalExpiredCTA proposalId={proposal.onchainId} slug={slug} />
    }

    return null
  }

  const ctaComponent = getCTAComponent()

  if (!ctaComponent) return null

  return (
    <Flex flex={1} {...flexProps}>
      {ctaComponent}
    </Flex>
  )
}

export default ProposalOnChainCTA
